// ============================================================================
// SetPasswordScreen — Phase 2.1.5
// ============================================================================
// Invite / password-recovery
// landing screen. Manually exchanges URL-hash access_token/refresh_token for a
// session because the supabase client is initialized with
// detectSessionInUrl:false. CRITICAL path — forgot-password flow gate.
// Preserve this screen's setSession/updateUser sequence and the client setting
// pinned by tests/static/auth_persistence_static.test.js.
// ============================================================================
import React, {useState} from 'react';
import Button from '../shared/Button.js';
import Field from '../shared/Field.js';
import InlineNotice from '../shared/InlineNotice.js';
import AuthScreenFrame from './AuthScreenFrame.js';

import {sb} from '../lib/supabase.js';
import {useAppLocation, useAppNavigate} from '../app/navigation/index.js';

type SetPasswordScreenProps = {
  onDone: () => void;
  prefilledEmail?: string | null;
};

function SetPasswordScreen({onDone, prefilledEmail}: SetPasswordScreenProps) {
  const location = useAppLocation();
  const navigate = useAppNavigate();
  // updateUser is async.  Keep the URL cleanup aligned with the location at
  // completion time (the legacy code read window.location then), not the
  // location captured when the recovery screen first rendered.
  const latestLocation = React.useRef(location);
  latestLocation.current = location;
  const latestNavigate = React.useRef(navigate);
  latestNavigate.current = navigate;
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  // Supabase JS exchanges the recovery token for a session asynchronously.
  // The screen can render before that finishes (we trigger it from the URL
  // hash on mount), so we have to wait for a real session before allowing
  // updateUser — otherwise it fails with "Auth session missing".
  const [sessionReady, setSessionReady] = useState(false);
  const [linkInvalid, setLinkInvalid] = useState(false);

  React.useEffect(() => {
    let mounted = true;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    // The supabase client is initialized with detectSessionInUrl:false (see
    // top of file) so the auto-exchange that would normally run on init is
    // disabled. We have to manually pick up the recovery tokens from the
    // URL and call setSession ourselves, otherwise updateUser fails with
    // "Auth session missing".
    async function tryEstablishSession() {
      try {
        const {
          data: {session: existing},
        } = await sb.auth.getSession();
        if (mounted && existing) {
          setSessionReady(true);
          clearTimeout(timeoutId);
          return;
        }
        // Implicit-flow tokens land in the URL hash
        const hash = (window.location.hash || '').replace(/^#/, '');
        if (hash) {
          const params = new URLSearchParams(hash);
          const at = params.get('access_token');
          const rt = params.get('refresh_token');
          if (at && rt) {
            const {error: sErr} = await sb.auth.setSession({access_token: at, refresh_token: rt});
            if (!sErr && mounted) {
              setSessionReady(true);
              clearTimeout(timeoutId);
              return;
            }
          }
        }
        // PKCE-flow code lands in the query string
        const code = new URLSearchParams(window.location.search).get('code');
        if (code) {
          const {error: cErr} = await sb.auth.exchangeCodeForSession(code);
          if (!cErr && mounted) {
            setSessionReady(true);
            clearTimeout(timeoutId);
            return;
          }
        }
      } catch (e) {
        /* fall through to timeout */
      }
    }
    tryEstablishSession();

    const {
      data: {subscription},
    } = sb.auth.onAuthStateChange((event, session) => {
      if (mounted && session) {
        setSessionReady(true);
        clearTimeout(timeoutId);
      }
    });
    // If we still haven't established a session after 8s, the recovery
    // link is expired or already used. Surface a clear error rather than
    // letting the user submit into the void.
    timeoutId = setTimeout(() => {
      if (mounted && !sessionReady) setLinkInvalid(true);
    }, 8000);
    return () => {
      mounted = false;
      subscription.unsubscribe();
      clearTimeout(timeoutId);
    };
  }, []);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError('');
    if (!sessionReady) {
      setError('Still validating your reset link \u2014 give it a moment and try again.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    const {error: upErr} = await sb.auth.updateUser({password});
    setLoading(false);
    if (upErr) {
      setError(upErr.message || 'Could not set password. The recovery link may have expired.');
      return;
    }
    // Clear any auth tokens left in the URL so a refresh doesn't re-trigger
    // recovery. Preserve the legacy explicit null history state through the
    // facade rather than writing browser history directly.
    try {
      const currentLocation = latestLocation.current;
      latestNavigate.current(currentLocation.pathname + currentLocation.search, {replace: true, state: null});
    } catch (_e: unknown) {
      /* cosmetic URL cleanup; auth state already set above */
    }
    setDone(true);
  }

  return (
    <AuthScreenFrame title="Set Password" email={prefilledEmail}>
      {done ? (
        <div className="wcf-auth-form">
          <InlineNotice notice={{kind: 'success', message: "Password set. You're signed in."}} />
          <Button variant="primary" fullWidth onClick={onDone}>
            Continue To Planner
          </Button>
        </div>
      ) : linkInvalid && !sessionReady ? (
        <div className="wcf-auth-form">
          <InlineNotice notice={{kind: 'error', message: 'This reset link is expired or has already been used.'}} />
          <p className="wcf-auth-help">
            Return to sign in and choose <strong>Forgot Password?</strong> to get a fresh link.
          </p>
          <a href="https://wcfplanner.com" className="wcf-auth-link">
            Back To Sign In
          </a>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="wcf-auth-form">
          <p className="wcf-auth-help">
            Pick a password with at least 6 characters. You'll use this and your email to sign in next time.
          </p>
          <Field label="New Password" required>
            <input
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
            />
          </Field>
          <Field label="Confirm Password" required>
            <input
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </Field>
          {!sessionReady && !error && (
            <p className="wcf-auth-help" role="status">
              Validating reset link…
            </p>
          )}
          <InlineNotice notice={error ? {kind: 'error', message: error} : null} />
          <Button type="submit" variant="primary" fullWidth disabled={!sessionReady} busy={loading} busyLabel="Saving…">
            {!sessionReady ? 'Verifying…' : 'Set Password'}
          </Button>
        </form>
      )}
    </AuthScreenFrame>
  );
}

export default SetPasswordScreen;
